import React, { useState, useEffect, useRef } from 'react';
import {
  UserPlus, Mail, Trash2, FileText, MessageSquare, Send,
  CheckCircle, Clock, RefreshCw, AlertCircle, Upload, Users,
  Download, Eye,
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../App';
import { getApiToken } from '../services/api';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

const STATUS_CONFIG = {
  open:    { label: 'Open',     color: '#f59e0b', bg: '#fffbeb', Icon: Clock },
  partial: { label: 'Partial',  color: '#3b82f6', bg: '#eff6ff', Icon: RefreshCw },
  closed:  { label: 'Complete', color: '#10b981', bg: '#ecfdf5', Icon: CheckCircle },
};

export default function DealPortalTab({ dealId, canEdit, currentUser }) {
  const [view, setView] = useState('overview'); // overview | messages | requests
  const [invites, setInvites] = useState([]);
  const [portalDocs, setPortalDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('seller');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Request form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqTitle, setReqTitle] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqPriority, setReqPriority] = useState('medium');
  const [reqDue, setReqDue] = useState('');
  const [reqInviteId, setReqInviteId] = useState('');
  const [savingReq, setSavingReq] = useState(false);

  // Messages
  const [msgContent, setMsgContent] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const msgEndRef = useRef();
  const msgPollRef = useRef();
  const docPollRef = useRef();

  const token = getApiToken();

  useEffect(() => {
    loadAll();
  }, [dealId]);

  // Poll portal docs + requests every 8s while on overview tab
  useEffect(() => {
    if (view === 'overview') {
      docPollRef.current = setInterval(() => loadPortalDocs(), 8000);
    }
    return () => clearInterval(docPollRef.current);
  }, [view]);

  useEffect(() => {
    if (view === 'messages') {
      scrollToBottom();
      msgPollRef.current = setInterval(() => loadMessages(), 5000);
    }
    return () => clearInterval(msgPollRef.current);
  }, [view]);

  const scrollToBottom = () =>
    setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [invRes, docRes, reqRes, msgRes] = await Promise.all([
        axios.get(`${API_URL}/api/deals/${dealId}/portal/invites`, { headers: authHeaders(token) }),
        axios.get(`${API_URL}/api/deals/${dealId}/portal/documents`, { headers: authHeaders(token) }),
        axios.get(`${API_URL}/api/deals/${dealId}/document-requests`, { headers: authHeaders(token) }),
        axios.get(`${API_URL}/api/deals/${dealId}/portal/messages`, { headers: authHeaders(token) }),
      ]);
      setInvites(invRes.data || []);
      setPortalDocs(docRes.data || []);
      setRequests(reqRes.data?.requests || []);
      setMessages(msgRes.data || []);
    } catch (err) {
      setError('Failed to load portal data.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/deals/${dealId}/portal/messages`,
        { headers: authHeaders(token) }
      );
      setMessages(data || []);
    } catch {}
  };

  const loadPortalDocs = async () => {
    try {
      const [docRes, reqRes] = await Promise.all([
        axios.get(`${API_URL}/api/deals/${dealId}/portal/documents`, { headers: authHeaders(token) }),
        axios.get(`${API_URL}/api/deals/${dealId}/document-requests`, { headers: authHeaders(token) }),
      ]);
      setPortalDocs(docRes.data || []);
      setRequests(reqRes.data?.requests || []);
    } catch {}
  };

  // ── Invite ────────────────────────────────────────────────
  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      await axios.post(
        `${API_URL}/api/deals/${dealId}/portal/invites`,
        { email: inviteEmail, displayName: inviteName, role: inviteRole },
        { headers: authHeaders(token) }
      );
      setInviteSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail(''); setInviteName(''); setInviteRole('seller');
      setShowInviteForm(false);
      const { data } = await axios.get(
        `${API_URL}/api/deals/${dealId}/portal/invites`,
        { headers: authHeaders(token) }
      );
      setInvites(data || []);
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invite.');
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    if (!window.confirm('Revoke this access? The portal user will no longer be able to log in.')) return;
    try {
      await axios.delete(
        `${API_URL}/api/deals/${dealId}/portal/invites/${inviteId}`,
        { headers: authHeaders(token) }
      );
      setInvites(prev => prev.map(i => i.id === inviteId ? { ...i, status: 'revoked' } : i));
    } catch {}
  };

  // ── Requests ─────────────────────────────────────────────
  const createRequest = async (e) => {
    e.preventDefault();
    if (!reqTitle.trim()) return;
    setSavingReq(true);
    try {
      await axios.post(
        `${API_URL}/api/deals/${dealId}/document-requests`,
        {
          title: reqTitle,
          description: reqDesc,
          priority: reqPriority,
          dueDate: reqDue || undefined,
          portalInviteId: reqInviteId || undefined,
        },
        { headers: authHeaders(token) }
      );
      setReqTitle(''); setReqDesc(''); setReqPriority('medium'); setReqDue(''); setReqInviteId('');
      setShowRequestForm(false);
      const { data } = await axios.get(
        `${API_URL}/api/deals/${dealId}/document-requests`,
        { headers: authHeaders(token) }
      );
      setRequests(data?.requests || []);
    } catch {}
    finally { setSavingReq(false); }
  };

  const updateRequestStatus = async (reqId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/deals/${dealId}/document-requests/${reqId}`,
        { status },
        { headers: authHeaders(token) }
      );
      setRequests(prev => prev.map(r => r.id === reqId ? { ...r, status } : r));
    } catch {}
  };

  const deleteRequest = async (reqId) => {
    try {
      await axios.delete(
        `${API_URL}/api/deals/${dealId}/document-requests/${reqId}`,
        { headers: authHeaders(token) }
      );
      setRequests(prev => prev.filter(r => r.id !== reqId));
    } catch {}
  };

  // ── Messages ─────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgContent.trim() || sendingMsg) return;
    setSendingMsg(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/deals/${dealId}/portal/messages`,
        { content: msgContent.trim() },
        { headers: authHeaders(token) }
      );
      setMessages(prev => [...prev, data]);
      setMsgContent('');
      scrollToBottom();
    } catch {}
    finally { setSendingMsg(false); }
  };

  if (loading) return <div className="portal-tab-loading">Loading portal…</div>;
  if (error)   return <div className="portal-tab-error"><AlertCircle size={16} /> {error}</div>;

  const activeInvites = invites.filter(i => i.status !== 'revoked');
  const unreadMsgCount = messages.filter(m => m.sender_type === 'portal').length;

  return (
    <div className="portal-tab-root">

      {/* Sub-nav */}
      <div className="portal-tab-subnav">
        {[
          { id: 'overview', label: 'Overview', icon: Users },
          { id: 'requests', label: 'Requests', icon: CheckCircle, badge: requests.filter(r => r.status === 'open').length },
          { id: 'messages', label: 'Messages', icon: MessageSquare, badge: messages.filter(m => m.sender_type === 'portal').length },
        ].map(item => (
          <button
            key={item.id}
            className={`portal-tab-subnav-btn ${view === item.id ? 'active' : ''}`}
            onClick={() => setView(item.id)}
          >
            <item.icon size={15} />
            {item.label}
            {item.badge > 0 && <span className="portal-tab-badge">{item.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {view === 'overview' && (
        <div className="portal-tab-section">

          {/* Invite panel */}
          <div className="portal-tab-card">
            <div className="portal-tab-card-header">
              <div>
                <h3>Portal Access</h3>
                <p>Invite sellers or advisors to upload documents and communicate on this deal.</p>
              </div>
              {canEdit && (
                <button className="portal-tab-btn" onClick={() => setShowInviteForm(v => !v)}>
                  <UserPlus size={15} /> Invite
                </button>
              )}
            </div>

            {inviteSuccess && (
              <div className="portal-tab-alert success"><CheckCircle size={14} /> {inviteSuccess}</div>
            )}

            {showInviteForm && (
              <form className="portal-tab-form" onSubmit={sendInvite}>
                <div className="portal-tab-form-row">
                  <div className="portal-tab-form-group">
                    <label>Email *</label>
                    <input
                      type="email" required placeholder="seller@company.com"
                      value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    />
                  </div>
                  <div className="portal-tab-form-group">
                    <label>Name (optional)</label>
                    <input
                      type="text" placeholder="John Smith"
                      value={inviteName} onChange={e => setInviteName(e.target.value)}
                    />
                  </div>
                  <div className="portal-tab-form-group" style={{ flex: '0 0 140px' }}>
                    <label>Role</label>
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                      <option value="seller">Seller</option>
                      <option value="advisor">Advisor</option>
                    </select>
                  </div>
                </div>
                {inviteError && <div className="portal-tab-alert error">{inviteError}</div>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="portal-tab-btn" disabled={inviting}>
                    <Mail size={14} /> {inviting ? 'Sending…' : 'Send Invite'}
                  </button>
                  <button type="button" className="portal-tab-btn secondary" onClick={() => setShowInviteForm(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {invites.length === 0 ? (
              <div className="portal-tab-empty">
                <Users size={28} color="#d1d5db" />
                <p>No portal invites yet. Invite a seller to get started.</p>
              </div>
            ) : (
              <div className="portal-tab-invite-list">
                {invites.map(inv => (
                  <div key={inv.id} className={`portal-tab-invite-row ${inv.status}`}>
                    <div className="portal-tab-invite-info">
                      <span className="portal-tab-invite-email">{inv.email}</span>
                      <span className="portal-tab-invite-role">{inv.role}</span>
                    </div>
                    <div className="portal-tab-invite-meta">
                      <span className={`portal-tab-invite-status ${inv.status}`}>
                        {inv.status === 'pending' ? 'Invite sent' : inv.status === 'accepted' ? 'Active' : 'Revoked'}
                      </span>
                      {inv.status !== 'revoked' && canEdit && (
                        <button
                          className="portal-tab-icon-btn danger"
                          onClick={() => revokeInvite(inv.id)}
                          title="Revoke access"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seller documents feed */}
          <div className="portal-tab-card">
            <div className="portal-tab-card-header">
              <div>
                <h3>Seller Documents</h3>
                <p>Files uploaded by portal users. Showing latest versions only.</p>
              </div>
            </div>
            {portalDocs.length === 0 ? (
              <div className="portal-tab-empty">
                <FileText size={28} color="#d1d5db" />
                <p>No documents uploaded yet.</p>
              </div>
            ) : (
              <div className="portal-tab-doc-list">
                {portalDocs.map(doc => (
                  <div key={doc.id} className="portal-tab-doc-row">
                    <FileText size={16} color="#6b7280" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="portal-tab-doc-name">{doc.filename}</div>
                      <div className="portal-tab-doc-meta">
                        v{doc.version} · {doc.uploadedBy || doc.uploaderEmail} ·{' '}
                        {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`portal-tab-doc-role ${doc.uploaderRole}`}>{doc.uploaderRole}</span>
                    <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
                      <a
                        href={`${API_URL}/api/deals/${dealId}/portal/documents/${doc.id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="portal-tab-icon-btn"
                        title="View"
                        onClick={e => {
                          e.preventDefault();
                          const url = `${API_URL}/api/deals/${dealId}/portal/documents/${doc.id}/download`;
                          fetch(url, { headers: authHeaders(token) })
                            .then(r => r.blob())
                            .then(blob => window.open(URL.createObjectURL(blob), '_blank'));
                        }}
                      >
                        <Eye size={14} />
                      </a>
                      <a
                        href={`${API_URL}/api/deals/${dealId}/portal/documents/${doc.id}/download?download=1`}
                        className="portal-tab-icon-btn"
                        title="Download"
                        onClick={e => {
                          e.preventDefault();
                          const url = `${API_URL}/api/deals/${dealId}/portal/documents/${doc.id}/download?download=1`;
                          fetch(url, { headers: authHeaders(token) })
                            .then(r => r.blob())
                            .then(blob => {
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = doc.filename;
                              a.click();
                            });
                        }}
                      >
                        <Download size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Requests ── */}
      {view === 'requests' && (
        <div className="portal-tab-section">
          <div className="portal-tab-card">
            <div className="portal-tab-card-header">
              <div>
                <h3>Document Requests</h3>
                <p>Push checklists to portal users. They see these and can upload directly against them.</p>
              </div>
              {canEdit && (
                <button className="portal-tab-btn" onClick={() => setShowRequestForm(v => !v)}>
                  + New Request
                </button>
              )}
            </div>

            {showRequestForm && (
              <form className="portal-tab-form" onSubmit={createRequest}>
                <div className="portal-tab-form-row">
                  <div className="portal-tab-form-group" style={{ flex: 2 }}>
                    <label>Title *</label>
                    <input
                      type="text" required placeholder="e.g. Last 3 years P&L statements"
                      value={reqTitle} onChange={e => setReqTitle(e.target.value)}
                    />
                  </div>
                  <div className="portal-tab-form-group">
                    <label>Priority</label>
                    <select value={reqPriority} onChange={e => setReqPriority(e.target.value)}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="portal-tab-form-group">
                    <label>Due date</label>
                    <input type="date" value={reqDue} onChange={e => setReqDue(e.target.value)} />
                  </div>
                </div>
                <div className="portal-tab-form-group">
                  <label>Description</label>
                  <input
                    type="text" placeholder="Additional context for the seller"
                    value={reqDesc} onChange={e => setReqDesc(e.target.value)}
                  />
                </div>
                {activeInvites.length > 0 && (
                  <div className="portal-tab-form-group">
                    <label>Assign to portal user (optional)</label>
                    <select value={reqInviteId} onChange={e => setReqInviteId(e.target.value)}>
                      <option value="">— Any portal user —</option>
                      {activeInvites.map(inv => (
                        <option key={inv.id} value={inv.id}>{inv.email} ({inv.role})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="portal-tab-btn" disabled={savingReq}>
                    {savingReq ? 'Saving…' : 'Add Request'}
                  </button>
                  <button type="button" className="portal-tab-btn secondary" onClick={() => setShowRequestForm(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {requests.length === 0 ? (
              <div className="portal-tab-empty">
                <CheckCircle size={28} color="#d1d5db" />
                <p>No document requests yet.</p>
              </div>
            ) : (
              <div className="portal-tab-request-list">
                {requests.map(req => {
                  const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                  const StatusIcon = cfg.Icon;
                  return (
                    <div key={req.id} className="portal-tab-request-row">
                      <div className="portal-tab-request-left">
                        <span className="portal-tab-request-status" style={{ color: cfg.color, background: cfg.bg }}>
                          <StatusIcon size={12} /> {cfg.label}
                        </span>
                        <div>
                          <div className="portal-tab-request-title">{req.title}</div>
                          {req.description && <div className="portal-tab-request-desc">{req.description}</div>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="portal-tab-request-actions">
                          {req.status !== 'closed' && (
                            <button
                              className="portal-tab-btn-xs"
                              onClick={() => updateRequestStatus(req.id, 'closed')}
                            >
                              Close
                            </button>
                          )}
                          {req.status === 'closed' && (
                            <button
                              className="portal-tab-btn-xs secondary"
                              onClick={() => updateRequestStatus(req.id, 'open')}
                            >
                              Reopen
                            </button>
                          )}
                          <button
                            className="portal-tab-icon-btn danger"
                            onClick={() => deleteRequest(req.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Messages ── */}
      {view === 'messages' && (
        <div className="portal-tab-messages">
          <div className="portal-tab-messages-list">
            {messages.length === 0 ? (
              <div className="portal-tab-empty" style={{ margin: 'auto' }}>
                <MessageSquare size={28} color="#d1d5db" />
                <p>No messages yet.</p>
              </div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`portal-tab-message ${msg.sender_type === 'internal' ? 'mine' : 'theirs'}`}
                >
                  <div className="portal-tab-message-name">
                    {msg.sender_name}
                    {msg.sender_type === 'portal' && (
                      <span className="portal-tab-message-tag">Seller</span>
                    )}
                  </div>
                  <div className="portal-tab-message-bubble">{msg.content}</div>
                  <div className="portal-tab-message-time">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))
            )}
            <div ref={msgEndRef} />
          </div>
          <form className="portal-tab-message-form" onSubmit={sendMessage}>
            <input
              className="portal-tab-message-input"
              type="text"
              placeholder="Message the seller…"
              value={msgContent}
              onChange={e => setMsgContent(e.target.value)}
              maxLength={4000}
            />
            <button
              type="submit"
              className="portal-tab-message-send"
              disabled={!msgContent.trim() || sendingMsg}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

    </div>
  );
}
